import { useMemo } from "react";

import {
    formatLeverage,
    getLeverageLoopDirection,
    LoopExpanded,
    useOracleHistoricalPrices,
    usePrincipalCollateralBorrowRate
} from "@bridgesplit/abf-react";
import {
    BRAND_COLORS,
    Column,
    Dot,
    FormatType,
    Icon,
    Row,
    StatColumn,
    StatProps,
    Text,
    TimeseriesChart,
    useAppPalette
} from "@bridgesplit/ui";
import { filterTruthy, formatDate, formatUsd, nearestHour, roundTimestamp } from "@bridgesplit/utils";

import { useLoopContext } from "../../LoopContext";
import { useWindContext } from "../../wind";

type PerformanceInfo = {
    date: number;
    price: number;
    leveragedValue: number;
};

export default function LoopPerformanceHistory({
    period,
    setPeriod,
    xDomain
}: {
    period: number;
    setPeriod: (p: number) => void;
    xDomain: [number, number];
}) {
    const data = useLoopPerformanceHistory(period);
    const { secondary } = useAppPalette();
    const { loopExpanded } = useLoopContext();
    const {
        form: { multiplier }
    } = useWindContext();

    const startDate = data?.[0]?.date;
    const isShort = isShortExposure(loopExpanded);
    const leverage = formatLeverage(multiplier ?? loopExpanded?.loopVault.maxLeverage ?? 1);
    const tokenSymbol = isShort ? loopExpanded?.principalToken.symbol : loopExpanded?.collateralToken.symbol;
    const actionVerb = isShort ? "Short" : "Long";

    return (
        <TimeseriesChart<PerformanceInfo>
            chartId="loop-performance-history"
            data={data ?? []}
            height={200}
            axisFormat={FormatType.Usd}
            xDomain={xDomain}
            series={[
                { dataKey: "leveragedValue", label: `${leverage} ${actionVerb} Net Value`, color: secondary },
                { dataKey: "price", label: `${tokenSymbol ?? ""} Price`, color: BRAND_COLORS.GRAY }
            ]}
            renderTooltip={({ axisValue, dataPoint }) => {
                if (!dataPoint || axisValue === undefined) return null;
                return (
                    <Stats
                        axisValue={axisValue}
                        dataPoint={dataPoint}
                        leverage={leverage}
                        actionVerb={actionVerb}
                        tokenSymbol={tokenSymbol}
                        startDate={startDate}
                        secondary={secondary}
                    />
                );
            }}
            period={period}
            setPeriod={setPeriod}
        />
    );
}

type StatsProps = {
    axisValue: number;
    dataPoint: PerformanceInfo;
    leverage: string;
    actionVerb: string;
    tokenSymbol: string | undefined;
    startDate: number | undefined;
    secondary: string;
};

function getStats({
    leverage,
    actionVerb,
    tokenSymbol,
    dataPoint,
    secondary
}: Pick<StatsProps, "leverage" | "actionVerb" | "tokenSymbol" | "dataPoint" | "secondary">): StatProps[] {
    return [
        {
            caption: `${leverage} ${actionVerb} Net Value`,
            value: (
                <Text variant="body2">
                    <Dot color={secondary} /> {formatUsd(dataPoint.leveragedValue)}
                </Text>
            ),
            captionVariant: "body2"
        },
        {
            caption: `${tokenSymbol ?? ""} Price`,
            value: (
                <Text variant="body2">
                    <Dot color={BRAND_COLORS.GRAY} /> {formatUsd(dataPoint.price)}
                </Text>
            ),
            captionVariant: "body2"
        }
    ];
}

function Stats({ axisValue, dataPoint, leverage, actionVerb, tokenSymbol, startDate, secondary }: StatsProps) {
    const stats = getStats({ leverage, actionVerb, tokenSymbol, dataPoint, secondary });
    return (
        <Column sx={{ minWidth: "240px", p: 1 }} spacing={0.5}>
            <Text variant="body2" color="disabled">
                <Icon type="date" />
                <Row spacing={0.25}>
                    {formatDate(startDate, "date", { showTime: "never" })}
                    <Icon type="forward" /> {formatDate(axisValue)}
                </Row>
            </Text>
            <StatColumn stats={dataPoint.leveragedValue < dataPoint.price ? stats.reverse() : stats} />
        </Column>
    );
}

function isShortExposure(loopExpanded: LoopExpanded | undefined): boolean {
    return loopExpanded ? getLeverageLoopDirection(loopExpanded) === "Short" : false;
}

function useLoopPerformanceHistory(period: number) {
    const { loopExpanded } = useLoopContext();
    const {
        form: { multiplier }
    } = useWindContext();

    const collateralMint = loopExpanded?.collateralToken.mint;
    const principalMint = loopExpanded?.principalToken.mint;
    const leverage = multiplier ?? loopExpanded?.loopVault.maxLeverage ?? 1;
    const isShort = isShortExposure(loopExpanded);

    const { data: priceData } = useOracleHistoricalPrices({
        mints: [principalMint, collateralMint].filter(filterTruthy),
        startTimestamp: nearestHour() - period
    });

    const { data: borrowRatesRecord } = usePrincipalCollateralBorrowRate(
        {
            principalMint,
            collateralMints: collateralMint ? [collateralMint] : undefined,
            timeLookback: period
        },
        { skip: !loopExpanded || !collateralMint }
    );
    const borrowRates = collateralMint ? borrowRatesRecord?.perCollateral?.[collateralMint] : undefined;

    return useMemo(() => {
        if (
            !collateralMint ||
            !principalMint ||
            !priceData ||
            !borrowRates?.length ||
            !priceData[principalMint]?.length ||
            !priceData[collateralMint]?.length
        ) {
            return undefined;
        }

        const collateralPrices = priceData[collateralMint];
        const principalPrices = priceData[principalMint];

        const collateralMap = new Map(collateralPrices.map((p) => [roundTimestamp(p.startTimestamp), p.avgPrice]));
        const principalMap = new Map(principalPrices.map((p) => [roundTimestamp(p.startTimestamp), p.avgPrice]));
        const borrowRateMap = new Map(
            borrowRates
                .filter((row) => row.wAvgActiveBorrowRate != null)
                .map((row) => [roundTimestamp(row.timestamp), row.wAvgActiveBorrowRate as number])
        );

        const data: PerformanceInfo[] = [];
        const seen = new Set<number>();
        let lastTimestamp: number | undefined = undefined;
        let cumBorrowCost = 0;
        let initialPrincipal: number | undefined = undefined;
        let initialCollateral: number | undefined = undefined;

        for (const { startTimestamp } of collateralPrices) {
            const ts = roundTimestamp(startTimestamp);
            if (seen.has(ts)) continue;
            seen.add(ts);

            const r = borrowRateMap.get(ts);
            const principalPrice = principalMap.get(ts);
            const collateralPrice = collateralMap.get(ts);
            if (r == null || !principalPrice || !collateralPrice) continue;

            // Calculate initial position on first iteration using current prices
            if (initialPrincipal === undefined) {
                const initialDepositUsd = isShort ? principalPrice : collateralPrice;
                initialPrincipal = ((leverage - 1) * initialDepositUsd) / principalPrice;
                initialCollateral = (leverage * initialDepositUsd) / collateralPrice;
            }

            if (initialPrincipal === undefined || initialCollateral === undefined) {
                continue;
            }

            const dt = ts - (lastTimestamp ?? ts);
            cumBorrowCost += ((initialPrincipal * r) / 31556926) * dt;
            lastTimestamp = ts;

            const debtUsd = (initialPrincipal + cumBorrowCost) * principalPrice;
            const collateralUsd = initialCollateral * collateralPrice;
            const positionValue = collateralUsd - debtUsd;

            data.push({
                date: ts,
                price: isShort ? principalPrice : collateralPrice,
                leveragedValue: positionValue
            });
        }

        return data;
    }, [collateralMint, principalMint, priceData, borrowRates, isShort, leverage]);
}
